import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import { Images } from "../../../utils/Images";

const BENEFITS_OF_CROWDFUNDING_CMS_SECTION_ID = 97;

const BenefitsOfCrowdfunding = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // <600px
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md")); // 600–960px
  const isDesktop = useMediaQuery(theme.breakpoints.up("md")); // ≥960px

  const home = useSelector((state) => state.cms.home);
  const cms = useMemo(() => {
    const sections = Array.isArray(home?.data) ? home.data : [];
    const section = sections.find((s) => s?.id === BENEFITS_OF_CROWDFUNDING_CMS_SECTION_ID);
    if (!section || section.status === 0) return null;
    return {
      title: section.title ?? "",
      sub_title: section.sub_title ?? "",
      relatedCMS: Array.isArray(section.relatedCMS) ? section.relatedCMS.filter((r) => r?.status === 1) : [],
    };
  }, [home]);

  const sectionTitle = cms?.title ?? "";
  const sectionDescription = cms?.sub_title ?? "";

  // Top 3 cards from CMS relatedCMS (title = value, sub_title = label); bottom 3 icon cards stay static
  const benefits = useMemo(() => {
    const topCards = (cms?.relatedCMS ?? [])
      .slice(0, 3)
      .map((item, i) => ({
        id: item.id ?? `cms-${i}`,
        value: item.title ?? "",
        label: item.sub_title ?? "",
        type: "number",
      }));
    const iconCards = [
      { id: 4, value: "Fast Review of Fundraisers", label: "", type: "icon", icon: Images.feedback },
      { id: 5, value: "Indian Tax Benefits", label: "", type: "icon", icon: Images.bag },
      { id: 6, value: "100% Transparency", label: "", type: "icon", icon: Images.opacity },
    ];
    return [...topCards, ...iconCards];
  }, [cms?.relatedCMS]);

  // Responsive styles: mobile (2-col), tablet, desktop (3-col grid)
  const getResponsiveStyles = () => {
    if (isMobile) {
      return {
        container: {
          width: "100%",
          paddingTop: "20px",
          paddingBottom: "20px",
          paddingLeft: "16px",
          paddingRight: "16px",
        },
        title: { fontSize: "18px", lineHeight: "16.18px", letterSpacing: "-2%", marginBottom: "10px", paddingLeft: "0px" },
        description: { fontSize: "10px", lineHeight: "19.42px", letterSpacing: "-2%", marginBottom: "20px", paddingLeft: "0px" },
        sectionHeader: { fontSize: "10px", lineHeight: "100%", letterSpacing: "2px", marginLeft: "0px", marginBottom: "10px" },
      };
    }
    if (isTablet) {
      return {
        container: {
          width: "94.44%",
          maxWidth: "1440px",
          borderRadius: "24px",
          paddingTop: "28px",
          paddingBottom: "40px",
          paddingLeft: "24px",
          paddingRight: "24px",
        },
        title: { fontSize: "28px", lineHeight: "36px", letterSpacing: "-2%", marginBottom: "8px", paddingLeft: "0px" },
        description: { fontSize: "14px", lineHeight: "21px", letterSpacing: "-2%", marginBottom: "28px", paddingLeft: "0px" },
        sectionHeader: { fontSize: "13px", lineHeight: "100%", letterSpacing: "2px", marginLeft: "0px", marginBottom: "14px" },
      };
    }
    return {
      container: {
        width: "94.44%",
        maxWidth: "1440px",
        borderRadius: "36px",
        paddingTop: "50px",
        paddingBottom: "50px",
        paddingLeft: "40px",
        paddingRight: "40px",
      },
      title: { fontSize: "36px", lineHeight: "46px", letterSpacing: "-2%", marginBottom: "12px", paddingLeft: "0px" },
      description: { fontSize: "16px", lineHeight: "24px", letterSpacing: "-2%", marginBottom: "60px", paddingLeft: "0px" },
      sectionHeader: { fontSize: "14px", lineHeight: "100%", letterSpacing: "2px", marginLeft: "0px", marginBottom: "24px" },
    };
  };

  const styles = getResponsiveStyles();

  return (
    <Box
      sx={{
        ...styles.container,
        maxWidth: "1440px",
        background: "#F7F7F7",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        marginTop: isMobile ? "20px" : "40px",
        marginBottom: isMobile ? "30px" : "60px",
      }}
    >
      {/* BENEFITS OF CROWDFUNDING */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: isMobile ? "8px" : "10px",
          alignSelf: "flex-start",
          marginLeft: styles.sectionHeader.marginLeft,
          marginBottom: styles.sectionHeader.marginBottom,
        }}
      >
        <Box
          sx={{
            width: isMobile ? "6px" : "8px",
            height: isMobile ? "6px" : "8px",
            borderRadius: "50%",
            backgroundColor: "#A9C74E",
          }}
        />
        <Typography
          sx={{
            fontFamily: "Arial",
            fontWeight: 400,
            fontSize: styles.sectionHeader.fontSize,
            lineHeight: styles.sectionHeader.lineHeight,
            letterSpacing: styles.sectionHeader.letterSpacing,
            verticalAlign: "middle",
            textTransform: "uppercase",
            color: "#131313",
          }}
        >
          BENEFITS OF CROWDFUNDING
        </Typography>
      </Box>

      {/* Title */}
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: styles.title.fontSize,
          lineHeight: styles.title.lineHeight,
          letterSpacing: styles.title.letterSpacing,
          verticalAlign: "middle",
          textTransform: "capitalize",
          color: "#1D1D1B",
          paddingLeft: styles.title.paddingLeft,
          alignSelf: "flex-start",
          marginBottom: styles.title.marginBottom,
          textAlign: "center",
        }}
      >
        {sectionTitle}
      </Typography>

      {/* Description */}
      <Typography
        sx={{
          fontWeight: 400,
          fontSize: styles.description.fontSize,
          lineHeight: styles.description.lineHeight,
          letterSpacing: styles.description.letterSpacing,
          verticalAlign: "middle",
          color: "#7D7D7D",
          maxWidth: isMobile ? "100%" : "975px",
          textAlign: "left",
          alignSelf: "flex-start",
          paddingLeft: styles.description.paddingLeft,
          marginBottom: styles.description.marginBottom,
        }}
      >
        {sectionDescription}
      </Typography>

      {/* Benefits Grid: mobile 2 per row, tablet/desktop 3 per row */}
      <Box
        sx={{
          display: "grid",
          width: "100%",
          gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(3, 1fr)",
          gridTemplateRows: "auto",
          gap: 0,
          position: "relative",
        }}
      >
        {benefits.map((benefit, index) => {
          const cols = isMobile ? 2 : 3;
          const row = Math.floor(index / cols);
          const col = index % cols;
          const isLastCol = col === cols - 1;
          const isLastRow = row === Math.ceil(benefits.length / cols) - 1;
          return (
            <Box
              key={benefit.id}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: isMobile ? "120px" : "133px",
                padding: isMobile ? "20px 12px" : isTablet ? "24px 16px" : "30px 20px",
                borderRight: isLastCol ? "none" : "1px solid #E8E8E8",
                borderBottom: isLastRow ? "none" : "1px solid #E8E8E8",
              }}
            >
              {benefit.type === "number" ? (
                <>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: isMobile ? "28px" : isTablet ? "36px" : "42px",
                      lineHeight: isMobile ? "36px" : isTablet ? "44px" : "52px",
                      letterSpacing: "0%",
                      color: "#A9C74E",
                      mb: isMobile ? "8px" : "12px",
                      textAlign: "center",
                    }}
                  >
                    {benefit.value}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: isMobile ? "14px" : "16px",
                      lineHeight: isMobile ? "20px" : "22px",
                      letterSpacing: "0%",
                      color: "#1D1D1B",
                      textAlign: "center",
                    }}
                  >
                    {benefit.label}
                  </Typography>
                </>
              ) : (
                <>
                  <Box
                    component="img"
                    src={benefit.icon}
                    alt={benefit.value}
                    sx={{
                      width: isMobile ? "48px" : "56px",
                      height: isMobile ? "48px" : "56px",
                      objectFit: "contain",
                      mb: isMobile ? "12px" : "16px",
                    }}
                  />
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: isMobile ? "14px" : "16px",
                      lineHeight: isMobile ? "20px" : "22px",
                      letterSpacing: "0%",
                      color: "#1D1D1B",
                      textAlign: "center",
                    }}
                  >
                    {benefit.value}
                  </Typography>
                </>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default BenefitsOfCrowdfunding;

